import { useState, useEffect } from 'react';
import { fetchTourWeather, weatherInfo, daysUntil, FORECAST_MAX_DAYS } from '../../utils/weather';
import './WeatherCard.css';

/**
 * Forecast for a tour's date at its meeting point / start.
 * Renders nothing if we have no usable coordinate or date.
 */
export function WeatherCard({ lat, lng, date }) {
  const [state, setState] = useState({ status: 'loading' }); // loading | ok | unavailable | future

  useEffect(() => {
    if (lat == null || lng == null || !date) { setState({ status: 'hidden' }); return; }
    const days = daysUntil(date);
    if (days < 0)                  { setState({ status: 'unavailable', msg: 'Tour date has passed' }); return; }
    if (days > FORECAST_MAX_DAYS)  { setState({ status: 'future' }); return; }

    setState({ status: 'loading' });
    const ctl = new AbortController();
    fetchTourWeather(lat, lng, date, ctl.signal).then(w => {
      if (ctl.signal.aborted) return;
      setState(w ? { status: 'ok', w } : { status: 'unavailable', msg: 'Forecast unavailable' });
    });
    return () => ctl.abort();
    // Depend on the timestamp, not the Date object — callers often build `date`
    // fresh on every render (`new Date(...)`), which would otherwise refetch
    // the forecast on every unrelated re-render even though the day is unchanged.
  }, [lat, lng, date?.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === 'hidden') return null;

  return (
    <div className="weather-card">
      <span className="weather-card__heading">Forecast</span>

      {state.status === 'loading' && (
        <div className="weather-card__muted"><span className="weather-card__spinner" /> Checking…</div>
      )}

      {state.status === 'future' && (
        <div className="weather-card__muted">Available ~{FORECAST_MAX_DAYS} days before the ride</div>
      )}

      {state.status === 'unavailable' && (
        <div className="weather-card__muted">{state.msg}</div>
      )}

      {state.status === 'ok' && <WeatherRow w={state.w} />}

      {state.status === 'ok' && (state.w.sunrise || state.w.sunset) && (
        <div className="weather-card__daylight">
          <span className="weather-card__sun" title="Sunrise">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="8 6 12 2 16 6"/></svg>
            {state.w.sunrise}
          </span>
          <span className="weather-card__sun" title="Sunset">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="9" x2="12" y2="2"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="16 5 12 9 8 5"/></svg>
            {state.w.sunset}
          </span>
        </div>
      )}
    </div>
  );
}

function WeatherRow({ w }) {
  const { icon, label } = weatherInfo(w.code);
  const wet = w.precip != null && w.precip >= 50;
  const windy = w.wind >= 35;
  return (
    <div className="weather-card__body">
      <span className="weather-card__icon">{icon}</span>
      <div className="weather-card__main">
        <span className="weather-card__label">{label}</span>
        <span className="weather-card__temp">{w.tMax}° <span className="weather-card__temp-min">/ {w.tMin}°</span></span>
      </div>
      <div className="weather-card__meta">
        {w.precip != null && (
          <span className={`weather-card__chip${wet ? ' weather-card__chip--warn' : ''}`}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>
            {w.precip}%
          </span>
        )}
        <span className={`weather-card__chip${windy ? ' weather-card__chip--warn' : ''}`}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>
          {w.wind} km/h
        </span>
      </div>
    </div>
  );
}
